/**
 * @fileoverview Shared Lennard-Jones (12-6) + Coulomb pair primitives.
 *
 * These are the lowest-level nonbonded building blocks, consumed by both the
 * ligand-internal nonbonded kernel (`forces/nonbonded.js`) and the static
 * receptor kernel (`forces/receptor.js`) — owned by other agents. Keeping
 * them here means there is exactly one definition of the LJ/Coulomb energy
 * and its `gradient/r` factor, so the two callers cannot drift apart.
 *
 * Contract:
 *   - Units are engine-internal: length nm, energy kJ/mol, charge e.
 *   - Each pair routine takes the **squared** separation `r2` (so the caller,
 *     which already computed it for the cutoff test, doesn't recompute) and
 *     writes two outputs into a caller-provided `Float64Array(2)`:
 *       out2[0] = pair energy (kJ/mol)
 *       out2[1] = gOverR — the scalar such that the force-derived gradient on
 *                 atom i is `gOverR * (xi - xj)` and on atom j is its negative.
 *     i.e. gOverR = (1/r) * dE/dr. With `d = xi - xj` and `r = |d|`,
 *     `dE/dxi = (dE/dr)(d/r) = gOverR * d`.
 *   - **No allocation** (out2 is reused by the caller across the inner loop).
 *   - **Unshifted, unscaled, no cutoff.** Callers apply the cutoff, the
 *     potential shift, and 1-4 scaling. LJ and Coulomb get *different* 1-4
 *     factors (AMBER scnb vs scee), which is exactly why they are kept as two
 *     separate routines rather than a single fused pair term.
 */

/**
 * Coulomb constant in kJ·nm/(mol·e^2). This is 1/(4 pi eps0) expressed in
 * engine units; it equals OpenMM's ONE_4PI_EPS0 (138.935458). With charges in
 * e and distance in nm, `KE_COULOMB * qi * qj / r` is in kJ/mol.
 * @type {number}
 */
export const KE_COULOMB = 138.935458;

/**
 * Lorentz combining rule for sigma: arithmetic mean.
 * @param {number} si   sigma_i (nm).
 * @param {number} sj   sigma_j (nm).
 * @returns {number}    combined sigma_ij (nm).
 */
export function combineSigma(si, sj) {
    return (si + sj) / 2;
}

/**
 * Berthelot combining rule for epsilon: geometric mean.
 * @param {number} ei   epsilon_i (kJ/mol).
 * @param {number} ej   epsilon_j (kJ/mol).
 * @returns {number}    combined epsilon_ij (kJ/mol).
 */
export function combineEps(ei, ej) {
    return Math.sqrt(ei * ej);
}

/**
 * Lennard-Jones 12-6 pair energy and gradient factor.
 *
 *     E   = 4 eps [ (sigma/r)^12 - (sigma/r)^6 ]
 *     dE/dr = 4 eps [ -12 sigma^12 / r^13 + 6 sigma^6 / r^7 ]
 *     gOverR = (1/r) dE/dr = 4 eps [ -12 sigma^12 / r^14 + 6 sigma^6 / r^8 ]
 *            = (24 eps / r^2) [ s6 - 2 s12 ]   where s6 = (sigma/r)^6, s12 = s6^2
 *
 * The grouped form above is computed entirely from `r2`, avoiding any sqrt.
 *
 * @param {number} r2         Squared separation (nm^2). Must be > 0.
 * @param {number} sigmaIJ    Combined sigma (nm).
 * @param {number} epsIJ      Combined epsilon (kJ/mol).
 * @param {Float64Array} out2 Caller-provided length-2 output: [E_lj, gOverR_lj].
 * @returns {void}
 */
export function ljPair(r2, sigmaIJ, epsIJ, out2) {
    if (epsIJ === 0 || sigmaIJ === 0) {
        out2[0] = 0;
        out2[1] = 0;
        return;
    }
    const sigma2 = sigmaIJ * sigmaIJ;
    const sr2 = sigma2 / r2; // (sigma/r)^2
    const sr6 = sr2 * sr2 * sr2; // (sigma/r)^6
    const sr12 = sr6 * sr6; // (sigma/r)^12

    out2[0] = 4 * epsIJ * (sr12 - sr6);
    // gOverR = (24 eps / r^2) * (sr6 - 2 sr12)
    out2[1] = ((24 * epsIJ) / r2) * (sr6 - (2 * sr12));
}

/**
 * Coulomb pair energy and gradient factor.
 *
 *     E   = KE_COULOMB * qq / r
 *     dE/dr = -KE_COULOMB * qq / r^2
 *     gOverR = (1/r) dE/dr = -KE_COULOMB * qq / r^3
 *
 * @param {number} r2         Squared separation (nm^2). Must be > 0.
 * @param {number} qq         Product of charges q_i * q_j (e^2).
 * @param {Float64Array} out2 Caller-provided length-2 output: [E_coul, gOverR_coul].
 * @returns {void}
 */
export function coulombPair(r2, qq, out2) {
    if (qq === 0) {
        out2[0] = 0;
        out2[1] = 0;
        return;
    }
    const r = Math.sqrt(r2);
    const e = (KE_COULOMB * qq) / r;
    out2[0] = e;
    // gOverR = -KE_COULOMB*qq / r^3 = -(E) / r^2
    out2[1] = -e / r2;
}
